import * as Location from 'expo-location';
import { Coordinate } from '@/experiences/sales-portal/components/map/types';
import { isWeb } from '@/utils/navigation';
import { withTimeout } from '@/utils/with-timeout';

const LOCATION_TIMEOUT_MS = 5000;

const toCoordinate = ({ coords }: Location.LocationObject): Coordinate => ({
  latitude: coords.latitude,
  longitude: coords.longitude,
});

const isPermissionGranted = async () => {
  try {
    const { status } = await Location.getForegroundPermissionsAsync();
    return status === Location.PermissionStatus.GRANTED;
  } catch {
    return false;
  }
};

const readGrantedLocation = async (
  onLocation: (location: Coordinate) => void,
) => {
  if (!(await isPermissionGranted())) {
    return;
  }
  let hasExactFix = false;
  await Promise.all([
    Location.getLastKnownPositionAsync()
      .then((position) => {
        if (position && !hasExactFix) {
          onLocation(toCoordinate(position));
        }
      })
      .catch(() => undefined),
    Location.getCurrentPositionAsync()
      .then((position) => {
        hasExactFix = true;
        onLocation(toCoordinate(position));
      })
      .catch(() => undefined),
  ]);
};

/**
 * Reads the device position from an already granted permission, leaving the
 * OS prompt to the primer. Reads the last known fix and the exact position in
 * parallel, so a quick Save still has coordinates and a slow last known read
 * cannot hold up the exact one.
 * @param onLocation - receives each fix as it arrives, at most twice. Nothing
 * arrives on web, without the grant, or on a failure or 5 s timeout.
 */
export const readCheckInLocation = async (
  onLocation: (location: Coordinate) => void,
): Promise<void> => {
  if (isWeb) {
    return;
  }
  await withTimeout(readGrantedLocation(onLocation), LOCATION_TIMEOUT_MS).catch(
    () => undefined,
  );
};
